import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Activity, Boxes, Flame, Fuel, Layers, ReceiptText, TrendingUp, Sparkles } from "lucide-react";
import { api } from "@/lib/api";
import SearchBar from "@/components/SearchBar";
import TxTable from "@/components/TxTable";
import { AddrLink, Card, EmptyState, SkeletonRows, StatCard, TokenLogo } from "@/components/common";
import { fmtCompact, fmtNum, fmtUsd, timeAgo } from "@/lib/format";

const REFRESH_MS = 15000;

export default function Home() {
  const [stats, setStats] = useState(null);
  const [blocks, setBlocks] = useState(null);
  const [txs, setTxs] = useState(null);
  const [tokens, setTokens] = useState(null);
  const [stocks, setStocks] = useState(null);

  useEffect(() => {
    let alive = true;
    const load = () => {
      api.get("/stats").then((r) => alive && setStats(r.data)).catch(() => {});
      api.get("/blocks").then((r) => alive && setBlocks(r.data.items?.slice(0, 6))).catch(() => alive && setBlocks([]));
      api.get("/transactions", { params: { filter: "validated" } }).then((r) => alive && setTxs(r.data.items?.slice(0, 8))).catch(() => alive && setTxs([]));
    };
    load();
    api.get("/tokens").then((r) => alive && setTokens(r.data.items?.slice(0, 8))).catch(() => alive && setTokens([]));
    api.get("/stock-tokens").then((r) => alive && setStocks(r.data.items?.slice(0, 6))).catch(() => alive && setStocks([]));
    const t = setInterval(load, REFRESH_MS);
    return () => { alive = false; clearInterval(t); };
  }, []);

  return (
    <div className="space-y-8">
      <section className="pt-12 pb-4 max-w-3xl">
        <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight" style={{ fontFamily: "Outfit" }}>
          Explore Robinhood Chain <span className="text-primary">clearly</span> and confidently.
        </h1>
        <p className="mt-4 text-sm sm:text-base text-muted-foreground max-w-xl">
          Search wallets, transactions, blocks, tokens and stock tokens — explained in plain language.
        </p>
        <div className="mt-7"><SearchBar /></div>
      </section>

      <section className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard testId="stat-latest-block" icon={Boxes} label="Latest Block"
          value={stats ? fmtNum(stats.latest_block, { maximumFractionDigits: 0 }) : "…"} sub="Auto-updating" />
        <StatCard testId="stat-total-txs" icon={ReceiptText} label="Total Transactions"
          value={stats ? fmtCompact(stats.total_transactions) : "…"} sub={stats ? `${fmtCompact(stats.transactions_today)} today` : ""} />
        <StatCard testId="stat-gas" icon={Fuel} label="Avg Gas Price"
          value={stats?.gas_prices ? `${stats.gas_prices.average} gwei` : "…"} sub="Typical fee < $0.01" />
        <StatCard testId="stat-activity" icon={Activity} label="Network Activity"
          value={stats ? fmtCompact(stats.total_addresses) : "…"}
          sub={stats ? `addresses · ETH ${fmtUsd(stats.coin_price)} (CoinGecko)` : ""} />
      </section>

      <section className="grid lg:grid-cols-2 gap-6">
        <Card data-testid="latest-blocks-card">
          <div className="flex items-center justify-between px-4 pt-4 pb-2">
            <h2 className="text-base font-semibold flex items-center gap-2"><Layers size={15} className="text-primary" /> Latest Blocks</h2>
            <Link to="/blocks" className="text-xs text-primary hover:underline" data-testid="view-all-blocks">View all</Link>
          </div>
          {!blocks ? <SkeletonRows n={6} /> : blocks.length === 0 ? <EmptyState /> : (
            <div className="divide-y">
              {blocks.map((b) => (
                <div key={b.height} className="flex items-center gap-3 px-4 py-3 table-row-hover" data-testid="block-row">
                  <span className="h-9 w-9 rounded-lg bg-primary/10 text-primary flex items-center justify-center text-[10px] font-mono shrink-0">BK</span>
                  <div className="min-w-0">
                    <Link to={`/block/${b.height}`} className="text-sm text-primary hover:underline font-mono">#{b.height}</Link>
                    <p className="text-xs text-muted-foreground">{timeAgo(b.timestamp)}</p>
                  </div>
                  <div className="ml-auto text-right">
                    <p className="text-xs">{b.transactions_count ?? 0} txns</p>
                    <p className="text-[10px] text-muted-foreground">by <AddrLink hash={b.miner?.hash} chars={4} /></p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </Card>

        <Card data-testid="latest-txs-card">
          <div className="flex items-center justify-between px-4 pt-4 pb-2">
            <h2 className="text-base font-semibold flex items-center gap-2"><ReceiptText size={15} className="text-primary" /> Latest Transactions</h2>
            <Link to="/txs" className="text-xs text-primary hover:underline" data-testid="view-all-txs">View all</Link>
          </div>
          <TxTable items={txs} loading={txs === null} compact />
        </Card>
      </section>

      <section className="grid lg:grid-cols-2 gap-6">
        <Card data-testid="trending-tokens-card">
          <div className="flex items-center justify-between px-4 pt-4 pb-2">
            <h2 className="text-base font-semibold flex items-center gap-2"><TrendingUp size={15} className="text-primary" /> Trending Tokens</h2>
            <Link to="/tokens" className="text-xs text-primary hover:underline" data-testid="view-all-tokens">View all</Link>
          </div>
          {!tokens ? <SkeletonRows n={6} /> : tokens.length === 0 ? <EmptyState /> : (
            <div className="divide-y">
              {tokens.map((t) => (
                <Link key={t.address_hash} to={`/token/${t.address_hash}`} className="flex items-center gap-3 px-4 py-3 table-row-hover" data-testid="trending-token-row">
                  <TokenLogo url={t.icon_url} symbol={t.symbol} size={28} />
                  <div className="min-w-0">
                    <p className="text-sm truncate">{t.name}</p>
                    <p className="text-[10px] text-muted-foreground uppercase">{t.symbol} · {fmtCompact(t.holders_count)} holders</p>
                  </div>
                  <div className="ml-auto text-right">
                    <p className="text-sm font-mono">{t.exchange_rate ? fmtUsd(t.exchange_rate) : <span className="text-muted-foreground text-xs">Price unavailable</span>}</p>
                    <p className="text-[10px] text-muted-foreground">Vol {t.volume_24h ? fmtUsd(t.volume_24h, 0) : "—"}</p>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </Card>

        <Card data-testid="stock-tokens-card">
          <div className="flex items-center justify-between px-4 pt-4 pb-2">
            <h2 className="text-base font-semibold flex items-center gap-2"><Sparkles size={15} className="text-primary" /> Stock Tokens</h2>
            <Link to="/stocks" className="text-xs text-primary hover:underline" data-testid="view-all-stocks">View all</Link>
          </div>
          {!stocks ? <SkeletonRows n={6} /> : stocks.length === 0 ? (
            <EmptyState title="No stock tokens found yet" subtitle="Tokenized equities will appear here once detected on Robinhood Chain." />
          ) : (
            <div className="divide-y">
              {stocks.map((t) => (
                <Link key={t.address_hash} to={`/token/${t.address_hash}`} className="flex items-center gap-3 px-4 py-3 table-row-hover" data-testid="stock-token-row">
                  <TokenLogo url={t.icon_url} symbol={t.symbol} size={28} />
                  <div className="min-w-0">
                    <p className="text-sm truncate">{t.company_name}</p>
                    <p className="text-[10px] text-muted-foreground uppercase">{t.symbol} · Tokenized equity</p>
                  </div>
                  <div className="ml-auto text-right">
                    <p className="text-sm font-mono">{t.exchange_rate ? fmtUsd(t.exchange_rate) : <span className="text-muted-foreground text-xs">Price unavailable</span>}</p>
                    <p className="text-[10px] text-muted-foreground">{fmtCompact(t.holders_count)} holders</p>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </Card>
      </section>

      <Card className="p-4 flex items-start gap-3">
        <Flame size={15} className="text-primary mt-0.5 shrink-0" />
        <p className="text-xs text-muted-foreground leading-relaxed">
          GMScan is an independent explorer and is not officially operated by Robinhood. Data comes live from the
          Robinhood Chain network and public indexers.
        </p>
      </Card>
    </div>
  );
}
